import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetAllConsoleWebsitesQuery,
  setSelectedConsoleWebsite,
  selectCurrentConsoleWebsiteId,
} from "../app/api/console/consoleWebsitesApiSlice";

function ConsoleWebsiteSelector() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const dispatch = useDispatch();

  const { data: websites = [], isLoading } = useGetAllConsoleWebsitesQuery();
  const selectedWebsiteId = useSelector(selectCurrentConsoleWebsiteId);

  const sortedAndFilteredWebsites = websites
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((website) =>
      website.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const selectedWebsite =
    websites.find(
      (website) => website.console_website_id === selectedWebsiteId
    ) || (websites.length > 0 ? websites[0] : null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (websites.length > 0 && !selectedWebsiteId) {
      dispatch(setSelectedConsoleWebsite(websites[0].console_website_id));
    } else if (
      selectedWebsiteId &&
      !websites.find((w) => w.console_website_id === selectedWebsiteId) &&
      websites.length > 0
    ) {
      dispatch(setSelectedConsoleWebsite(websites[0].console_website_id));
    }
  }, [websites, selectedWebsiteId, dispatch]);

  const handleWebsiteSelect = (website) => {
    dispatch(setSelectedConsoleWebsite(website.console_website_id));
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setSearchTerm("");
    }
  };

  if (isLoading) {
    return (
      <div className='h-8 w-48 bg-surface-neutral-subtle rounded-lg animate-pulse' />
    );
  }

  if (!websites.length || !selectedWebsite) {
    return <div className='text-text-tertiary text-body-sm'>No websites available</div>;
  }

  return (
    <div className='relative' ref={dropdownRef}>
      <button
        className='flex items-center gap-2 px-3 py-1.5 text-text-primary bg-transparent hover:bg-surface-neutral rounded-lg transition-colors'
        onClick={handleDropdownToggle}>
        <span className='text-[14px] font-semibold truncate max-w-[200px]'>{selectedWebsite.name}</span>
        <svg
          className={`w-4 h-4 text-text-tertiary transition-transform duration-200 ${
            isDropdownOpen ? "rotate-180" : ""
          }`}
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
          strokeWidth={2}>
          <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
        </svg>
      </button>

      {isDropdownOpen && (
        <div className='absolute left-0 w-72 mt-2 bg-white rounded-2xl shadow-lg border border-border-subtle z-50 overflow-hidden animate-fade-in'>
          <div className='p-2'>
            <input
              ref={searchInputRef}
              type='text'
              placeholder='Search websites...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='w-full px-3 py-2 text-body-sm bg-surface-neutral rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-accent-purple/30 placeholder:text-text-tertiary'
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className='max-h-56 overflow-auto px-1 pb-1 scrollbar-thin'>
            {sortedAndFilteredWebsites.length > 0 ? (
              sortedAndFilteredWebsites.map((website) => {
                const isSelected = selectedWebsiteId === website.console_website_id;
                return (
                  <button
                    key={website.console_website_id}
                    onClick={() => handleWebsiteSelect(website)}
                    className={`w-full text-left px-3 py-2 text-body-sm rounded-lg transition-colors ${
                      isSelected
                        ? "bg-surface-neutral font-medium text-text-primary"
                        : "text-text-secondary hover:bg-surface-neutral"
                    }`}>
                    <div className='flex items-center gap-2'>
                      {isSelected && (
                        <svg className='w-4 h-4 text-accent-purple flex-shrink-0' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
                          <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
                        </svg>
                      )}
                      <span className={isSelected ? "" : "ml-6"}>{website.name}</span>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className='px-3 py-4 text-body-sm text-text-tertiary text-center'>
                No websites found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ConsoleWebsiteSelector;
